import { format, parse } from 'date-fns';
import { type AvailabilityAddress } from './OpenFormAnswers.js';
import type { Address } from '../../generated/api/address';

const LOCALE_DATE = 'dd.MM.yyyy';
const LOCALE_DATETIME = 'dd.MM.yyyy HH:mm:ss';

export const capitalize = (input = '', separator = ' ') =>
  input
    .split(separator)
    .map(word => word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase())
    .join(separator);

export const concat = <T>(...input: T[] | never[]): Exclude<T, T[] | '' | null | undefined>[] =>
  input.flatMap(n => {
    switch (typeof n) {
      case 'number':
        return !Number.isNaN(n) ? n : [];
      case 'object':
        return Array.isArray(n) ? concat(...n) : n !== null ? n : [];
      case 'string':
        return n.length > 0 ? n : [];
      case 'undefined':
        return [];
      default:
        return n;
    }
  });

export const formatLocaleDate = (date: string | number | Date) => format(date, LOCALE_DATE);

export const formatLocaleDateTime = (date: string | number | Date) => format(date, LOCALE_DATETIME);

export const fromLocaleDate = (date: string) => parse(date, LOCALE_DATE, new Date());

export const fromLocaleDateTime = (date: string) => parse(date, LOCALE_DATETIME, new Date());

export const getAddressText = (addr: Address | undefined) =>
  addr ? concat(addr.line1, addr.line2, addr.postalCode, addr.postOffice).join(' ') : undefined;

export const getAvailabilityAddressDetails = (addr: AvailabilityAddress | undefined) =>
  addr?.match
    ? concat(
        addr.match.addressId,
        addr.match.buildingDescription,
        addr.match.statusOfBuilding,
        addr.match.apartmentType,
        addr.match.numberOfBusinessPremises
      ).join(' | ')
    : undefined;

export const getAvailabilityAddressText = (addr: AvailabilityAddress | undefined) =>
  addr?.match ? concat(addr.match.addressText, addr.match.postalCode, addr.match.postalOffice).join(' ') : undefined;

// `date` format is `LOCALE_DATE`, but sent to salesforce as `yyyy-MM-dd`
export const getDate = (date: string | undefined) => (date ? format(fromLocaleDate(date), 'yyyy-MM-dd') : undefined);

export const getValue = <T>(input?: T | T[]) =>
  !input ? input : Array.isArray(input) ? String(input) : typeof input === 'object' ? undefined : input;

export const isAvailabilityAddress = (addr: AvailabilityAddress | undefined): boolean => {
  return Boolean(
    addr?.match &&
      addr.match.postalCode &&
      addr.match.postalOffice &&
      addr.match.addressId &&
      addr.match.addressText &&
      addr.products
  );
};

export const isIframe = () => self !== (self.top ?? self.parent);

export const merge: {
  <
    Merge extends Record<Field, unknown> extends Record<Field, infer U> ? U : never,
    Field extends string | number | symbol = string | number | symbol,
    Input = unknown,
  >(
    callback: (value: Input, index: number, array: Input[]) => Merge,
    ...input: Input[]
  ): Merge;
} = (callback, ...input) => Object.assign({}, ...input.map(callback));

export const onChangePrice = (num = '') => num.replace(/^ *$/, '0.00').replace(/-/g, '').replace(/,/g, '.');

export const onBlurPrice = (num = '') => Math.max(0, Math.min(99999.99, Number(onChangePrice(num)))).toFixed(2);
